import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radio, RefreshCw, Search, PenLine, Check, X, Flag, Newspaper, MessageSquare, Cloud, AtSign, Loader2, ExternalLink, TrendingUp, ListFilter, Plus, Trash2, RotateCcw, Banknote, Megaphone } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as ChartTooltip, Legend } from "recharts";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { SentimentBadge } from "@/components/app/SentimentBadge";
import { Loader, EmptyState } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const STANCE_STYLE = {
  for_us: "bg-pos-bg text-pos-text",
  against_us: "bg-neg-bg text-neg-text",
  against_opponent: "bg-brand-navy/10 text-brand-navy",
  for_opponent: "bg-amber-50 text-amber-700",
};
const STANCE_LABEL = {
  for_us: "For us",
  against_us: "Against us",
  against_opponent: "Hits opponent",
  for_opponent: "Boosts opponent",
};

const SRC_ICONS = { news: Newspaper, reddit: MessageSquare, bluesky: Cloud, mastodon: AtSign, fec: Banknote, meta_ads: Megaphone };
const STATUS_STYLE = {
  new: "bg-brand-red/10 text-brand-red",
  triaged: "bg-brand-blue/10 text-brand-blue",
  actioned: "bg-pos-bg text-pos-text",
  dismissed: "bg-slate-100 text-slate-500",
};

export default function Feed() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ sentiment: "all", source_type: "all", topic: "all", status: "all", q: "" });

  const params = Object.fromEntries(
    Object.entries(filters).filter(([k, v]) => v && v !== "all" && !(k === "q" && !v))
  );

  const { data: facets } = useQuery({ queryKey: ["facets"], queryFn: () => api.get("/listening/feed/facets").then((r) => r.data) });
  const { data: items, isLoading } = useQuery({
    queryKey: ["feed", params],
    queryFn: () => api.get("/listening/feed", { params }).then((r) => r.data),
  });

  const poll = useMutation({
    mutationFn: () => api.post("/listening/poll").then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`${d.new_items} new stor${d.new_items === 1 ? "y" : "ies"} · ${d.fetched} fetched · ${d.gated_out ?? 0} filtered out · ${d.syndicated ?? 0} syndicated copies merged`);
      if (d.skipped_sources?.length) toast.info(`Skipped: ${d.skipped_sources.join("; ")}`);
      if (d.spike_alerts?.length) toast.warning(`Mention spike detected: ${d.spike_alerts.join(", ")}`);
      qc.invalidateQueries({ queryKey: ["feed"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["sov"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/listening/feed/${id}`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed"] });
      toast.success("Signal updated");
    },
  });

  return (
    <div>
      <PageHeader
        title="Listening Feed"
        subtitle="Live news + social signals (NewsData.io + Michigan press RSS, Google News, GDELT, Bluesky search, and the Bluesky Jetstream firehose) scored by relevance × recency. Triage, then turn a signal into content."
        icon={Radio}
        actions={
          hasRole("admin", "manager") && (
            <div className="flex flex-wrap items-center gap-3">
              <WatchlistSheet />
              <LiveStreamToggle />
              <AutoPollToggle />
              <Button data-testid="poll-sources-btn" onClick={() => poll.mutate()} disabled={poll.isPending} className="bg-brand-red text-white hover:bg-brand-redhover">
                {poll.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Poll sources
              </Button>
            </div>
          )
        }
      />

      <ShareOfVoice />

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-white p-3 shadow-sm">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-mute" />
          <Input
            data-testid="feed-search-input"
            placeholder="Search signals…"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className="pl-9"
          />
        </div>
        <FilterSelect testId="filter-sentiment" value={filters.sentiment} onChange={(v) => setFilters((f) => ({ ...f, sentiment: v }))} placeholder="Sentiment" options={["positive", "neutral", "negative"]} />
        <FilterSelect testId="filter-source" value={filters.source_type} onChange={(v) => setFilters((f) => ({ ...f, source_type: v }))} placeholder="Source" options={facets?.sources || []} />
        <FilterSelect testId="filter-topic" value={filters.topic} onChange={(v) => setFilters((f) => ({ ...f, topic: v }))} placeholder="Topic" options={facets?.topics || []} />
        <FilterSelect testId="filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} placeholder="Status" options={["new", "triaged", "actioned", "dismissed"]} />
      </div>

      {isLoading ? (
        <Loader label="Loading signals…" />
      ) : !items?.length ? (
        <EmptyState icon={Radio} title="No signals match" description="Try clearing filters or polling your sources for fresh items." />
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => {
            const Src = SRC_ICONS[it.source_type] || Newspaper;
            return (
              <div
                key={it.id}
                data-testid="feed-item-card"
                style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                className="animate-fade-up rounded-xl border border-border bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-brand-navy">
                      <Src className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-brand-navy">{it.author}</span>
                        <span className="text-xs text-brand-mute">· {it.geo} · {timeAgo(it.timestamp)}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase ${STATUS_STYLE[it.status]}`}>{it.status}</span>
                      </div>
                      <h3 className="mt-1 font-heading text-base font-bold text-brand-ink">{it.title}</h3>
                      <p className="mt-1 text-sm text-brand-mute">{it.text}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {it.stance && it.stance !== "neutral" && (
                          <span data-testid={`feed-stance-${it.id}`} className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold ${STANCE_STYLE[it.stance] || "bg-slate-100 text-slate-600"}`}>
                            {STANCE_LABEL[it.stance] || it.stance}
                          </span>
                        )}
                        {it.flag === "threat" && <span className="rounded-full bg-neg-bg px-2 py-0.5 text-[0.65rem] font-bold uppercase text-neg-text">Threat</span>}
                        {it.flag === "opportunity" && <span className="rounded-full bg-pos-bg px-2 py-0.5 text-[0.65rem] font-bold uppercase text-pos-text">Opportunity</span>}
                        {it.provider === "newsdata" && (
                          <Badge variant="outline" className="border-brand-blue/25 bg-brand-blue/[0.05] text-brand-blue" data-testid={`feed-provider-${it.id}`}>NewsData.io</Badge>
                        )}
                        {it.provider === "local_press" && (
                          <Badge variant="outline" className="border-brand-navy/30 bg-brand-navy/[0.06] text-brand-navy" data-testid={`feed-localpress-${it.id}`}>
                            Local press{it.press_region ? ` · ${it.press_region}` : ""}
                          </Badge>
                        )}
                        {it.live && (
                          <Badge variant="outline" className="border-brand-red/30 bg-brand-red/[0.06] text-brand-red" data-testid={`feed-live-${it.id}`}>Live stream</Badge>
                        )}
                        {it.topics?.map((t) => (
                          <Badge key={t} variant="outline" className="border-brand-navy/20 bg-brand-navy/[0.04] text-brand-navy">{t}</Badge>
                        ))}
                        {it.matched_entities?.map((m) => (
                          <Badge key={m} variant="outline" className="border-brand-red/25 bg-brand-red/[0.05] text-brand-red">{m}</Badge>
                        ))}
                        <span className="text-xs font-semibold text-brand-mute">
                          Relevance {it.relevanceScore}
                          {it.story_size > 1 ? ` · ${it.story_size} outlets` : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-3">
                    <SentimentBadge sentiment={it.sentiment} />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                  <a href={it.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline">
                    Source <ExternalLink className="h-3 w-3" />
                  </a>
                  <div className="flex items-center gap-2">
                    {hasRole("admin", "manager", "staff") ? (
                      <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`feed-status-${it.id}`}>
                          <Flag className="mr-1.5 h-3.5 w-3.5" /> Triage
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setStatus.mutate({ id: it.id, status: "triaged" })}><Flag className="mr-2 h-4 w-4" /> Mark triaged</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus.mutate({ id: it.id, status: "actioned" })}><Check className="mr-2 h-4 w-4" /> Mark actioned</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus.mutate({ id: it.id, status: "dismissed" })} className="text-brand-mute"><X className="mr-2 h-4 w-4" /> Dismiss</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button asChild size="sm" className="bg-brand-navy text-white hover:bg-brand-navyhover">
                      <Link to={`/studio?feed=${it.id}`} data-testid={`draft-response-${it.id}`}>
                        <PenLine className="mr-1.5 h-3.5 w-3.5" /> Draft response
                      </Link>
                    </Button>
                      </>
                    ) : (
                      <span className="text-xs text-brand-mute" data-testid={`feed-readonly-${it.id}`}>Read-only access</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EMPTY_ENTITY = { label: "", type: "issue", terms: "", context_terms: "", negative_terms: "", require_context: true };

function WatchlistSheet() {
  const qc = useQueryClient();
  const { data: entities } = useQuery({ queryKey: ["watchlist"], queryFn: () => api.get("/listening/watchlist").then((r) => r.data) });
  const [form, setForm] = useState(EMPTY_ENTITY);
  const [busy, setBusy] = useState(false);

  const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const refresh = () => qc.invalidateQueries({ queryKey: ["watchlist"] });

  const add = async () => {
    if (!form.label.trim()) return toast.error("Give the entity a name");
    setBusy(true);
    try {
      await api.post("/listening/watchlist", {
        label: form.label.trim(), type: form.type,
        terms: csv(form.terms).length ? csv(form.terms) : [form.label.trim()],
        context_terms: csv(form.context_terms), negative_terms: csv(form.negative_terms),
        require_context: form.require_context, enabled: true,
      });
      toast.success(`"${form.label}" added to the watchlist`);
      setForm(EMPTY_ENTITY);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (e) => {
    try {
      await api.patch(`/listening/watchlist/${e.id}`, { ...e, enabled: !e.enabled });
      refresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (e) => {
    try {
      await api.delete(`/listening/watchlist/${e.id}`);
      toast.success(`"${e.label}" removed`);
      refresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const reprocess = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/listening/reprocess?days=90");
      toast.success(`Re-examined ${data.reexamined} blocked signals: recovered ${data.recovered}, dismissed ${data.dismissed ?? 0} stale items`);
      qc.invalidateQueries({ queryKey: ["feed"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" data-testid="watchlist-open-btn">
          <ListFilter className="mr-2 h-4 w-4" /> Watchlist
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="watchlist-panel">
        <SheetHeader>
          <SheetTitle className="font-heading">Watchlist &amp; precision filters</SheetTitle>
          <SheetDescription className="text-xs">
            A signal enters the feed only when it matches an entity's terms, carries campaign context, and hits no negative terms. Edit rules, then re-scan recently blocked signals.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {(entities || []).map((e) => (
            <div key={e.id} className="rounded-lg border border-border p-3" data-testid={`watchlist-entity-${e.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Switch checked={!!e.enabled} onCheckedChange={() => toggle(e)} data-testid={`watchlist-toggle-${e.id}`} />
                  <span className="text-sm font-bold text-brand-ink">{e.label}</span>
                  <Badge variant="outline" className="capitalize">{e.type}</Badge>
                </div>
                <button onClick={() => remove(e)} className="text-brand-mute hover:text-brand-red" data-testid={`watchlist-delete-${e.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-1.5 text-[0.7rem] text-brand-mute">
                Terms: {e.terms?.join(", ") || "-"}
                {e.require_context && e.context_terms?.length ? ` · needs context (${e.context_terms.slice(0, 4).join(", ")}…)` : ""}
                {e.negative_terms?.length ? ` · blocks: ${e.negative_terms.slice(0, 3).join(", ")}…` : ""}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-5 space-y-3 rounded-lg border border-dashed border-border p-3">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-mute">Add entity</p>
          <Input placeholder="Name (e.g. Mike Rogers)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="entity-label-input" />
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="entity-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="candidate">Our candidate</SelectItem>
              <SelectItem value="opponent">Opponent</SelectItem>
              <SelectItem value="issue">Issue</SelectItem>
              <SelectItem value="organization">Organization</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Match terms, comma-separated (defaults to name)" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="entity-terms-input" />
          <Input placeholder="Required context words (senate, campaign…)" value={form.context_terms} onChange={(e) => setForm({ ...form, context_terms: e.target.value })} data-testid="entity-context-input" />
          <Input placeholder="Negative words (obituary, funeral…)" value={form.negative_terms} onChange={(e) => setForm({ ...form, negative_terms: e.target.value })} data-testid="entity-negative-input" />
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 text-xs">
              <Switch checked={form.require_context} onCheckedChange={(v) => setForm({ ...form, require_context: v })} /> Require context
            </Label>
            <Button size="sm" onClick={add} disabled={busy} className="bg-brand-navy text-white hover:bg-brand-navyhover" data-testid="watchlist-add-btn">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>

        <Button variant="outline" className="mt-4 w-full" onClick={reprocess} disabled={busy} data-testid="reprocess-btn">
          <RotateCcw className="mr-2 h-4 w-4" /> Re-scan blocked signals (90 days)
        </Button>
      </SheetContent>
    </Sheet>
  );
}

function LiveStreamToggle() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["streamStatus"],
    queryFn: () => api.get("/listening/stream/status").then((r) => r.data),
    refetchInterval: 15000,
  });
  const [busy, setBusy] = useState(false);

  const save = async (enabled) => {
    setBusy(true);
    try {
      await api.put("/listening/stream/config", { enabled });
      toast.success(
        enabled
          ? "Live streaming on, Bluesky posts matching your watchlist hit the feed within ~90s"
          : "Live streaming off"
      );
      qc.invalidateQueries({ queryKey: ["streamStatus"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const live = !!data?.connected;
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 shadow-sm"
      data-testid="live-stream-toggle"
    >
      <Switch checked={!!data?.enabled} disabled={busy} onCheckedChange={save} data-testid="live-stream-switch" />
      <span className="flex items-center gap-1.5 text-xs font-semibold text-brand-ink">
        <span
          className={`h-1.5 w-1.5 rounded-full ${live ? "animate-pulse bg-brand-red" : "bg-slate-300"}`}
          data-testid="live-stream-dot"
        />
        Live stream
      </span>
      {data?.enabled && (
        <span className="text-[0.65rem] text-brand-mute" data-testid="live-stream-stats">
          {live ? `${(data.events_seen || 0).toLocaleString()} seen · ${data.matched || 0} matched` : "connecting…"}
        </span>
      )}
    </div>
  );
}

function AutoPollToggle() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["listenConfig"], queryFn: () => api.get("/listening/config").then((r) => r.data) });
  const save = async (enabled) => {
    try {
      await api.put("/listening/config", { enabled, interval_minutes: data?.interval_minutes || 60 });
      toast.success(enabled ? "Auto-poll enabled, sources refresh every hour" : "Auto-poll disabled");
      qc.invalidateQueries({ queryKey: ["listenConfig"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 shadow-sm" data-testid="auto-poll-toggle">
      <Switch checked={!!data?.enabled} onCheckedChange={save} />
      <span className="text-xs font-semibold text-brand-ink">Auto-poll</span>
    </label>
  );
}

function ShareOfVoice() {
  const { data } = useQuery({ queryKey: ["sov"], queryFn: () => api.get("/listening/share-of-voice").then((r) => r.data) });
  if (!data || !data.total_items) return null;
  const total = Math.max(1, data.totals.us + data.totals.opponent);
  const usPct = Math.round((100 * data.totals.us) / total);
  return (
    <div className="mb-6 rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="share-of-voice-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-brand-red" />
          <h3 className="font-heading text-sm font-bold text-brand-ink">Share of voice, last {data.days || 14} days</h3>
        </div>
        <p className="text-xs text-brand-mute">
          <span className="font-bold text-brand-navy">{data.candidate}</span> {data.totals.us} mentions ({usPct}%) ·{" "}
          <span className="font-bold text-brand-red">{data.opponent}</span> {data.totals.opponent} ({100 - usPct}%) · {data.totals.issue} issue-only
        </p>
      </div>
      {data.series.length > 1 && (
        <div className="mt-3 h-36 min-h-[144px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={140}>
            <LineChart data={data.series} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6C757D" }} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#6C757D" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <ChartTooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="us" name={data.candidate} stroke="#1D3557" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="opponent" name={data.opponent} stroke="#E63946" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[150px]" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {placeholder.toLowerCase()}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
